import BackendService from 'BackendService';
import { backendErrorMessage } from '../../../services/baseBackendService/structuredErrors';
import React from 'react';
import { DropzoneRootProps, DropzoneInputProps } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import {
  AIAssistedCreationWizardProps,
  createInitialWizardState,
  WizardDocumentStatus,
  WizardState,
} from './aiAssistedCreationWizardTypes';
import {
  buildDocumentUrlsForGeneration,
  clearWizardLocalStorage,
  mergeDocumentStatusResults,
  transformApiDocuments,
} from './aiAssistedCreationWizardUtils';

export interface UseAIAssistedCreationWizardResult {
  state: WizardState;
  fundOpportunityId: string;
  dropzone: {
    getRootProps: <T extends DropzoneRootProps>(props?: T) => T;
    getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
    isDragActive: boolean;
  };
  handleDocumentSelection: (doc: WizardDocumentStatus, checked: boolean) => void;
  removeFile: (index: number) => void;
  handleGenerate: () => Promise<void>;
  handleExit: () => void;
  handleViewContent: () => void;
  handleNoViewContent: () => void;
}

export function useAIAssistedCreationWizard({
  open,
  fundOpportunity,
  onNavigateToContent,
  onExit,
  standalone = false,
}: AIAssistedCreationWizardProps): UseAIAssistedCreationWizardResult {
  const fundOpportunityId = fundOpportunity.id;

  const [state, setState] = React.useState<WizardState>(() => createInitialWizardState(standalone));

  React.useEffect(() => {
    if (standalone) {
      console.log('AI Wizard in standalone mode, current state:', state);
    }
  }, [standalone, state]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setState((prev) => ({
        ...prev,
        filesForUpload: [...prev.filesForUpload, ...acceptedFiles],
      }));
    },
    multiple: true,
  });

  React.useEffect(() => {
    if (!open) {
      return;
    }

    setState((prev) => ({ ...prev, isCheckingDocuments: true }));

    const backendService = new BackendService();
    backendService
      .getDocumentsForContentGeneration(fundOpportunityId)
      .then((apiDocuments) => {
        if (apiDocuments.length > 0) {
          console.log('Found documents from API, setting step to documentCheck', apiDocuments);
          const transformedDocs = transformApiDocuments(apiDocuments);

          if (standalone) {
            setState((prev) => ({
              ...prev,
              documents: transformedDocs,
              step: 'uploadAndGenerate',
              isCheckingDocuments: false,
            }));
          } else {
            setState((prev) => ({
              ...prev,
              documents: transformedDocs,
              isCheckingDocuments: false,
            }));
          }
        } else if (standalone) {
          setState((prev) => ({
            ...prev,
            step: 'uploadAndGenerate',
            isCheckingDocuments: false,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            isCheckingDocuments: false,
          }));
        }
      })
      .catch((error) => {
        console.error('Error fetching documents for content generation:', error);
        if (standalone) {
          setState((prev) => ({
            ...prev,
            step: 'uploadAndGenerate',
            isCheckingDocuments: false,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            isCheckingDocuments: false,
          }));
        }
      });
  }, [open, fundOpportunityId, standalone]);

  const checkDocumentStatus = React.useCallback(async () => {
    try {
      console.log('Checking document status for documents:', state.documents);
      const backendService = new BackendService();
      const result = await backendService.checkDocumentStatus(state.documents);
      console.log('Document status check result:', result);

      const documentsWithEncryptedUrl = mergeDocumentStatusResults(
        state.documents,
        result.documents,
      );
      const activeDocuments = documentsWithEncryptedUrl.filter((doc) => doc.status === 'active');

      setState((prev) => ({
        ...prev,
        documents: documentsWithEncryptedUrl,
        selectedDocuments: activeDocuments,
      }));
    } catch (error) {
      console.error('Error checking document status:', error);
      setState((prev) => ({
        ...prev,
        error: 'Failed to check document status. Please try again.',
      }));
    }
  }, [state.documents]);

  const checkDocumentStatusCalled = React.useRef(false);
  React.useEffect(() => {
    if (
      (state.step === 'documentCheck' || state.step === 'uploadAndGenerate') &&
      state.documents.length > 0 &&
      !checkDocumentStatusCalled.current
    ) {
      checkDocumentStatusCalled.current = true;
      checkDocumentStatus();
    }
    if (state.step !== 'documentCheck' && state.step !== 'uploadAndGenerate') {
      checkDocumentStatusCalled.current = false;
    }
  }, [state.step, state.documents.length, checkDocumentStatus]);

  const handleDocumentSelection = (doc: WizardDocumentStatus, checked: boolean) => {
    setState((prev) => {
      const newSelectedDocuments = checked
        ? [...prev.selectedDocuments, doc]
        : prev.selectedDocuments.filter((currentDoc) => currentDoc.name !== doc.name);
      return {
        ...prev,
        selectedDocuments: newSelectedDocuments,
      };
    });
  };

  const removeFile = (index: number) => {
    setState((prev) => ({
      ...prev,
      filesForUpload: prev.filesForUpload.filter((_, i) => i !== index),
    }));
  };

  const handleGenerate = async () => {
    if (state.filesForUpload.length === 0) {
      setState((prev) => ({ ...prev, error: 'Please add at least one PDF to upload.' }));
      return;
    }

    setState((prev) => ({
      ...prev,
      isUploading: true,
      error: null,
    }));

    try {
      const backendService = new BackendService();
      await Promise.all(
        state.filesForUpload.map((file) =>
          backendService.uploadDocumentToLibrary(
            file.name.replace(/\.pdf$/i, ''),
            '',
            fundOpportunityId,
            file,
          ),
        ),
      );

      localStorage.removeItem(`uploadedDocuments-${fundOpportunityId}`);

      setState((prev) => ({
        ...prev,
        isUploading: false,
        step: 'success',
      }));
    } catch (error) {
      console.error('Error uploading documents to library:', error);
      setState((prev) => ({
        ...prev,
        isUploading: false,
        error: backendErrorMessage(error, 'Failed to upload documents. Please try again.'),
      }));
    }
  };

  const handleExit = () => {
    clearWizardLocalStorage(fundOpportunityId);
    onExit();
  };

  const handleViewContent = () => {
    onNavigateToContent();
  };

  const handleNoViewContent = () => {
    onExit();
  };

  return {
    state,
    fundOpportunityId,
    dropzone: { getRootProps, getInputProps, isDragActive },
    handleDocumentSelection,
    removeFile,
    handleGenerate,
    handleExit,
    handleViewContent,
    handleNoViewContent,
  };
}
